import React, { Suspense, useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from '@cc/common/routing';
import { PropTypes } from 'prop-types';
import './LoginLayout.scss';

import {
    AlertBanner,
    BrandingLogos,
    ErrorBoundary,
    LoadingBar,
    RotatingDotsLoader,
    SiteLayoutAlert,
    SiteLayoutLoadingContainer,
} from '@cc/common';
import { useDispatch, useSelector } from 'react-redux';
import { finalizeLogout } from 'redux/slices/auth';

const LoginLayout = ({ children }) => {
    const [showLogout, setShowLogout] = useState(false);

    const { returnUrl } = useSelector((s) => s.auth);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        switch (location.pathname) {
            case '/':
                setShowLogout(false);
                break;
            case '/logout':
                setShowLogout(false);
                break;
            case '/login':
                setShowLogout(false);
                break;
            default:
                setShowLogout(true);
        }
    }, [location]);

    const logout = () => {
        dispatch(finalizeLogout());
        navigate('/');
    };

    return (
        <div variant="login" className="login-layout">
            <SiteLayoutAlert>
                <AlertBanner />
            </SiteLayoutAlert>
            <header className="login-layout__header">
                <BrandingLogos ccLogoSize="md" returnUrl={returnUrl} />
            </header>
            <SiteLayoutLoadingContainer>
                <LoadingBar isPromiseIndicator />
            </SiteLayoutLoadingContainer>
            <section className="login-layout__body">
                <ErrorBoundary>
                    <Suspense fallback={<RotatingDotsLoader hideText />}>{children}</Suspense>
                </ErrorBoundary>
            </section>
            <footer className="login-layout__footer">
                <h2 className="login-layout__footer-header">Questions?</h2>
                <div className="login-layout__footer-content contact-info">
                    <div className="contact-info__section">
                        <h3 className="contact-info__header">Contact Support</h3>
                        <p className="contact-info__detail">
                            <span className="contact-info__detail--italic">visit</span>{' '}
                            <a
                                className="contact-info__link"
                                href="https://carecontinuity.atlassian.net/servicedesk/customer/portal/2"
                                target="_blank"
                            >
                                Support Portal
                            </a>
                        </p>
                    </div>
                    <div className="contact-info__section contact-info__section--bordered">
                        <h3 className="contact-info__header">Operational Information</h3>
                        <p className="contact-info__detail">
                            <NavLink
                                className="contact-info__link contact-info__link--mr-4"
                                to="/PrivacyPolicy"
                                target="_blank"
                            >
                                Privacy Policy
                            </NavLink>
                            <NavLink
                                className="contact-info__link"
                                to="/TermsOfUse"
                                target="_blank"
                            >
                                Terms of Use
                            </NavLink>
                        </p>
                    </div>
                    <div className="contact-info__section">
                        <h3 className="contact-info__header">
                            Or, if you would like to learn more about us
                        </h3>
                        <p className="contact-info__detail">
                            <span className="contact-info__detail--italic">email</span>{' '}
                            <a
                                className="contact-info__link"
                                href="mailto:info@carecontinuity.com"
                                target="_blank"
                            >
                                Info
                            </a>{' '}
                            <span className="contact-info__detail--italic">or visit</span>{' '}
                            <a
                                className="contact-info__link"
                                href="http://www.carecontinuity.com/"
                                target="_blank"
                            >
                                www.carecontinuity.com
                            </a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

LoginLayout.propTypes = {
    children: PropTypes.node.isRequired,
};

export default LoginLayout;
